import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './Input';
import { menuItemVariants } from './MenuItem';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Secondary line — email, subdomain, whatever disambiguates two same-named rows. */
  sublabel?: string;
}

export interface SearchSelectProps {
  value: string | null;
  onChange: (value: string | null, option: SearchSelectOption | null) => void;

  /**
   * Runs debounced as the user types, and once with `''` when the field is first opened.
   * Should hit the server rather than filter a preloaded list — that is the whole point of this
   * component over `Select`.
   */
  onSearch: (query: string) => Promise<SearchSelectOption[]>;

  /**
   * Label for the current `value`. Required to render a selection whose option is not in the
   * latest result set — e.g. after a reload, or once the user types a query that excludes it.
   */
  selectedLabel?: string;

  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const DEBOUNCE_MS = 250;

/**
 * SearchSelect — a single-select combobox backed by an async, server-side search.
 *
 * Use this instead of `Select` whenever the option set is unbounded or merely large (accounts,
 * users, contacts). `Select` renders every option into the DOM, so it stops being usable — for the
 * reader and for the browser — somewhere in the low hundreds.
 *
 * Composed from `Input` and `menuItemVariants` so it inherits the same tokens, focus rings, and
 * hover treatment as the rest of the system rather than restating them.
 */
export const SearchSelect: React.FC<SearchSelectProps> = ({
  value,
  onChange,
  onSearch,
  selectedLabel,
  placeholder = 'Search…',
  emptyText = 'No matches',
  disabled,
  className,
  id,
}) => {
  const generatedId = useId();
  const listboxId = `${id ?? generatedId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SearchSelectOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Guards against a slow earlier request overwriting the results of a later one. */
  const requestSeq = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++requestSeq.current;
      setSearching(true);
      try {
        const results = await onSearch(q);
        if (seq === requestSeq.current) {
          setOptions(results);
          setActiveIndex(-1);
        }
      } catch {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    },
    [onSearch]
  );

  // Debounced search while open. Also fires once on open with an empty query, so the field shows
  // the first page of options without the user having to guess a search term.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => void runSearch(query), query ? DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [open, query, runSearch]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const select = (option: SearchSelectOption) => {
    onChange(option.value, option);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange(null, null);
    setQuery('');
    setOptions([]);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (options.length === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(prev => (prev + delta + options.length) % options.length);
      return;
    }

    if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && options[activeIndex]) {
        e.preventDefault();
        select(options[activeIndex]);
      }
      return;
    }

    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // When closed with a selection, the input shows the selection rather than the stale query.
  const displayValue = open ? query : (selectedLabel ?? '');

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />

        <Input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={displayValue}
          placeholder={selectedLabel && !open ? selectedLabel : placeholder}
          className="pl-9 pr-9"
          onFocus={() => setOpen(true)}
          onChange={e => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />

        {value && !open ? (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={clear}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-400">
            {searching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        )}
      </div>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-ink-200/80 bg-surface shadow-lg shadow-ink-200/50 py-1"
        >
          {searching && options.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-ink-500">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-ink-500">{emptyText}</div>
          ) : (
            options.map((option, index) => (
              // Raw button rather than MenuItem: a row inside a listbox is role="option", and
              // MenuItem hard-codes role="menuitem" (it Omits `role` from its props). Reusing
              // menuItemVariants keeps the styling identical without the wrong ARIA role.
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(option)}
                className={cn(menuItemVariants(), index === activeIndex && 'bg-ink-50')}
              >
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-ink-900">{option.label}</span>
                  {option.sublabel && (
                    <span className="block truncate text-13 text-ink-500">{option.sublabel}</span>
                  )}
                </span>
                {option.value === value && <Check className="h-4 w-4 text-accent-600 shrink-0" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

SearchSelect.displayName = 'SearchSelect';
